"use client";

import React from "react";
import { ArrowRight, Clock, MapPin, ShieldCheck } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
const serviceAreas = [{
  city: "Jonesboro, GA",
  county: "Clayton County",
  zipCodes: "30236, 30237, 30238",
  responseTime: "15-30 Mins",
  status: "Verified 24/7",
  path: "/jonesboro-service-area/"
}, {
  city: "Riverdale, GA",
  county: "Clayton County",
  zipCodes: "30274, 30296",
  responseTime: "20-35 Mins",
  status: "Verified 24/7",
  path: "/riverdale-service-area/"
}, {
  city: "Morrow, GA",
  county: "Clayton County",
  zipCodes: "30260, 30287",
  responseTime: "20-35 Mins",
  status: "Verified 24/7",
  path: "/morrow-service-area/"
}, {
  city: "Forest Park, GA",
  county: "Clayton County",
  zipCodes: "30297, 30298",
  responseTime: "25-40 Mins",
  status: "Verified 24/7",
  path: "/forest-park-service-area/"
}, {
  city: "Lovejoy, GA",
  county: "Clayton County",
  zipCodes: "30250",
  responseTime: "20-35 Mins",
  status: "Verified 24/7",
  path: "/lovejoy-service-area/"
}, {
  city: "Ellenwood, GA",
  county: "Clayton / Henry",
  zipCodes: "30294",
  responseTime: "25-40 Mins",
  status: "Verified 24/7",
  path: "/ellenwood-service-area/"
}, {
  city: "Stockbridge, GA",
  county: "Henry County",
  zipCodes: "30281",
  responseTime: "25-40 Mins",
  status: "Verified 24/7",
  path: "/stockbridge-service-area/"
}];
export default function Directory() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="serviceareadirectory" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-4 inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary border-primary/20 bg-primary/5">
            <MapPin className="size-3.5 text-accent" />
            Clayton &amp; Henry County Coverage
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Service Area Directory
          </h2>
          <p className="mt-4 font-sans text-base text-muted-foreground sm:text-lg">
            Rapid local response for burst pipes, sewer backups, and urgent water
            heater failures across South Metro Atlanta communities.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 25
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="mt-12 overflow-hidden rounded-xl border border-border bg-card shadow-md">
          <Table>
            <TableHeader className="bg-primary text-primary-foreground hover:bg-primary">
              <TableRow className="border-b border-primary/20 hover:bg-primary">
                <TableHead className="py-4 pl-6 text-primary-foreground font-semibold">
                  Service Community
                </TableHead>
                <TableHead className="hidden sm:table-cell py-4 text-primary-foreground font-semibold">
                  County
                </TableHead>
                <TableHead className="hidden md:table-cell py-4 text-primary-foreground font-semibold">
                  Zip Codes Covered
                </TableHead>
                <TableHead className="py-4 text-primary-foreground font-semibold">
                  Avg. Response
                </TableHead>
                <TableHead className="hidden lg:table-cell py-4 text-primary-foreground font-semibold">
                  Coverage Status
                </TableHead>
                <TableHead className="py-4 pr-6 text-right text-primary-foreground font-semibold">
                  Action
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {serviceAreas.map((area, i) => <TableRow key={area.city} data-index={i} className="even:bg-muted/40 hover:bg-muted/70 transition-colors border-b border-border">
                  <TableCell className="py-4.5 pl-6 font-medium text-foreground">
                    <div className="flex items-center gap-2">
                      <MapPin className="size-4 text-primary shrink-0" />
                      <span className="font-semibold">{area.city}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground sm:hidden">
                      {area.county} • {area.zipCodes}
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell py-4.5 text-muted-foreground font-sans">
                    {area.county}
                  </TableCell>
                  <TableCell className="hidden md:table-cell py-4.5 text-muted-foreground font-mono text-xs">
                    {area.zipCodes}
                  </TableCell>
                  <TableCell className="py-4.5 font-sans">
                    <div className="flex items-center gap-1.5 text-xs sm:text-sm font-medium text-foreground">
                      <Clock className="size-3.5 text-accent shrink-0" />
                      <span>{area.responseTime}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell py-4.5">
                    <Badge variant="secondary" className="inline-flex items-center gap-1 bg-secondary text-secondary-foreground text-xs font-medium">
                      <ShieldCheck className="size-3 text-primary" />
                      {area.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-4.5 pr-6 text-right">
                    <Button asChild size="sm" variant="outline" className="font-medium text-xs sm:text-sm group hover:bg-primary hover:text-primary-foreground transition-colors">
                      <Link to={area.path} className="inline-flex items-center gap-1.5">
                        <span>View Location</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
                      </Link>
                    </Button>
                  </TableCell>
                </TableRow>)}
            </TableBody>
          </Table>
        </motion.div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 rounded-lg bg-muted/60 p-4 sm:flex-row sm:px-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground text-center sm:text-left">
            <ShieldCheck className="size-5 text-primary shrink-0" />
            <span>
              All emergency dispatch routes are staffed with licensed, background-checked technicians ready 24 hours a day.
            </span>
          </div>
          <Button asChild variant="default" size="sm" className="shrink-0">
            <Link to="/contact/">Emergency Dispatch</Link>
          </Button>
        </div>
      </div>
    </section>;
}